import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { supabase } from "./supabaseClient"; 
import Navbar from "./components/Navbar";

// --- ALL IMPORTS ---
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import Leaderboard from "./pages/Leaderboard";
import Partners from "./pages/Partners";
import SDGs from "./pages/SDGs";
import SDGDetail from "./pages/SDGDetail";
import UserView from "./pages/UserView";
import SellerDashboard from "./pages/SellerDashboard";
import AdminPartners from "./pages/AdminPartners";
import Calendar from "./pages/Calendar";
import AdminCalendar from "./pages/AdminCalendar";
import GetCard from "./pages/get-card";
import Tracker from "./pages/Tracker";

function AppContent({ session, handleGoogleLogin, notification }) {
  const location = useLocation();
  const isExternalPath = location.pathname === "/seller" || location.pathname === "/tracker";

  return (
    <div style={{ 
      minHeight: "100vh", 
      position: "relative", 
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      backgroundColor: "#ffffff" 
    }}>
      
      {/* NOTIFICATION TOAST */}
      {notification && (
        <div style={{
          position: "fixed", top: "20px", left: "50%", transform: "translateX(-50%)",
          backgroundColor: "#12873B", color: "white", padding: "12px 24px",
          borderRadius: "50px", zIndex: 10000, fontWeight: "800", fontSize: "0.9rem",
          boxShadow: "0 8px 20px rgba(0,0,0,0.15)", display: "flex", alignItems: "center",
          gap: "8px", border: "2px solid white", pointerEvents: "none"
        }}>
          {notification}
        </div>
      )}

      {(session || isExternalPath) ? (
        <>
          <div style={{ paddingBottom: "80px" }}> 
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/sdgs" element={<SDGs />} />
              <Route path="/sdg/:id" element={<SDGDetail />} />
              <Route path="/user/:id" element={<UserView />} />
              <Route path="/seller" element={<SellerDashboard />} />
              <Route path="/tracker" element={<Tracker />} />
              <Route path="/adminpartners" element={<AdminPartners />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/admincalendar" element={<AdminCalendar/>} />
              <Route path="/get-card" element={<GetCard />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </div>
          {session && !isExternalPath && <Navbar />}
        </>
      ) : (
        /* --- MINIMALIST HUMMING LOGIN UI --- */
        <div style={{ 
          display: "flex", flexDirection: "column", alignItems: "center", 
          justifyContent: "center", height: "100vh", padding: "20px"
        }}>
          
          {/* REFINED BRAND TEXT */}
          <h1 style={{ 
            fontSize: "2rem", // Smaller, more balanced size
            fontWeight: "700", 
            marginBottom: "30px", 
            letterSpacing: "-0.5px", // Tighter, cleaner spacing
            background: "linear-gradient(135deg, #12873B 0%, #065F46 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textAlign: "center",
            opacity: 0.9
          }}>
            Humming'Card
          </h1>
          
          {/* THE PILL BUTTON */}
          <button 
            onClick={handleGoogleLogin}
            style={{ 
              display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
              width: "100%",
              maxWidth: "280px", // Slighly narrower button to match the text
              padding: "14px 24px", 
              background: "linear-gradient(135deg, #12873B 0%, #065F46 100%)", 
              color: "#fff", 
              border: "none", 
              borderRadius: "50px", 
              fontWeight: "600", 
              cursor: "pointer", 
              fontSize: "15px",
              boxShadow: "0 10px 20px rgba(18, 135, 59, 0.15)",
              transition: "all 0.3s ease"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 25px rgba(18, 135, 59, 0.25)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 10px 20px rgba(18, 135, 59, 0.15)";
            }}
          >
            <div style={{ 
              backgroundColor: "white", 
              borderRadius: "50%", 
              width: "24px", 
              height: "24px",
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
            }}>
              <img 
                src="https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/Logos/google%20logo.webp" 
                alt="Google" 
                width="14" 
              />
            </div>
            Connect with Google
          </button>

          <p style={{ marginTop: "40px", fontSize: "13px", color: "#999", letterSpacing: "0.2px" }}>
            Where little actions fly far
          </p>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user?.id) return;
    const channel = supabase
      .channel('realtime-points')
      .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          setNotification(`🎉 +${payload.new.points_awarded} Points received!`);
          setTimeout(() => setNotification(null), 4000);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [session]);

  const handleGoogleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
  };

  if (loading) return null;

  return (
    <Router>
      <AppContent 
        session={session} 
        handleGoogleLogin={handleGoogleLogin} 
        notification={notification} 
      />
    </Router>
  );
}